import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Download, 
  Star, 
  Calendar, 
  User,
  MessageSquare,
  RefreshCw,
  FileText,
  FileSpreadsheet,
  FileDown,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
// eslint-disable-next-line no-unused-vars
import { useToast } from './ToastContainer';
import api from '../utils/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './Reviews.css';

const Reviews = () => {
  const [reviews, setReviews] = useState([]);
  // Removed filteredReviews state since filtering is now handled by the backend
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [language, setLanguage] = useState('all');
  const [ratingFilter, setRatingFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsPerPage, setReviewsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateFilterType, setDateFilterType] = useState('range'); // 'single' or 'range'
  const [appliedStartDate, setAppliedStartDate] = useState('');
  const [appliedEndDate, setAppliedEndDate] = useState('');
  const [searchInput, setSearchInput] = useState(''); // Temporary input value
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  
  // Export popup states
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDateType, setExportDateType] = useState('all'); // 'all', 'quick', 'custom', 'range'
  const [exportQuickDate, setExportQuickDate] = useState('today');
  const [exportCustomDate, setExportCustomDate] = useState('');
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');

  const [exportSelectedBranches, setExportSelectedBranches] = useState([]);
  const [exportAllBranches, setExportAllBranches] = useState(true);
  const [exportColumns, setExportColumns] = useState({
    reviewer_name: true,
    stars: true,
    review_text: true,
    review_text_translated: true,
    published_at_date: true,
    place_title: true,
    city: true,
    country_code: true,
    original_language: true,
    response_from_owner_text: true,
    branch_name: true
  });
  const [exportFormat, setExportFormat] = useState('excel');
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Available columns for export
  const availableColumns = {
    reviewer_name: 'Reviewer Name',
    reviewer_photo_url: 'Reviewer Photo URL',
    reviewer_number_of_reviews: 'Reviewer Reviews Count',
    is_local_guide: 'Is Local Guide',
    review_text: 'Review Text',
    review_text_translated: 'Translated Review Text',
    review_title: 'Review Title',
    stars: 'Rating (Stars)',
    rating: 'Rating (Number)',
    likes_count: 'Likes Count',
    published_at_date: 'Published Date',
    response_from_owner_text: 'Owner Response',
    response_from_owner_date: 'Owner Response Date',
    review_image_urls: 'Review Images',
    place_title: 'Place Name',
    category_name: 'Category',
    address: 'Address',
    city: 'City',
    state: 'State',
    country_code: 'Country',
    original_language: 'Language',
    branch_name: 'Branch Name',
    review_id: 'Review ID',
    place_id: 'Place ID',
    total_score: 'Total Score',
    reviews_count: 'Place Reviews Count',
    price: 'Price Level'
  };

  // Quick date options
  const quickDateOptions = [
    { value: 'today', label: 'Today', icon: '📅' },
    { value: 'yesterday', label: 'Yesterday', icon: '📅' },
    { value: 'week', label: 'Last 7 Days', icon: '📅' },
    { value: 'month', label: 'Last 30 Days', icon: '📅' },
    { value: 'quarter', label: 'Last 3 Months', icon: '📅' },
    { value: 'year', label: 'Last Year', icon: '📅' }
  ];

  // Get the actual current date with proper timezone handling
  const getCurrentDate = () => {
    // Get the current date in the user's local timezone
    const now = new Date();
    
    // Create a date object for today at 00:00:00 local time
    // This ensures we get the correct date regardless of timezone
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    console.log('Current date calculation:', {
      rawNow: now.toString(),
      rawNowISO: now.toISOString(),
      localDateString: now.toLocaleDateString(),
      todayCalculated: today.toISOString(),
      year: now.getFullYear(),
      month: now.getMonth() + 1, // Add 1 since getMonth() returns 0-11
      day: now.getDate(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    
    return today;
  };

  // Export formats
  const exportFormats = [
    { value: 'excel', label: 'Excel (.xlsx)', icon: FileSpreadsheet },
    { value: 'csv', label: 'CSV (.csv)', icon: FileText },
    { value: 'pdf', label: 'PDF (.pdf)', icon: FileDown }
  ];

  useEffect(() => {
    fetchReviews();
    fetchBranches();
    // Initialize search input with current search term
    setSearchInput(searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchTerm, sortBy, language, ratingFilter, appliedStartDate, appliedEndDate, selectedBranches, reviewsPerPage]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout]);

  // Initialize export branches when branches are fetched
  useEffect(() => {
    if (branches.length > 0) {
      setExportSelectedBranches(branches.map(b => b.id));
    }
  }, [branches]);


  const fetchBranches = async () => {
    try {
      const response = await api.get('/api/branches');
      if (response.data.success) {
        setBranches(response.data.branches);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      setLoading(true);
      console.log('Fetching reviews from database...');
      console.log('Applied date filters:', { appliedStartDate, appliedEndDate });
      console.log('Selected branches:', selectedBranches);
      
      // Fetch reviews from the database with current filters
      const params = new URLSearchParams({
        page: currentPage,
        limit: reviewsPerPage,
        sortBy: sortBy,
        ...(searchTerm && { search: searchTerm }),
        ...(language !== 'all' && { language }),
        ...(ratingFilter !== 'all' && { rating: ratingFilter }),
        ...(appliedStartDate && { startDate: appliedStartDate }),
        ...(appliedEndDate && { endDate: appliedEndDate }),
        ...(selectedBranches.length > 0 && { branchIds: selectedBranches.join(',') })
      });
      
      console.log('Sending request with params:', params.toString());
      
      const response = await api.get(`/api/reviews?${params}`);
      
      if (response.data.success) {
        setReviews(response.data.reviews);
        setTotalCount(response.data.total);
        setTotalPages(response.data.pagination.totalPages);
        console.log(`Fetched ${response.data.reviews.length} reviews from database (${response.data.total} total)`);
      } else {
        console.error('Failed to fetch reviews from database');
        setReviews([]);
      }
    } catch (error) {
      console.error('Error fetching reviews from database:', error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const fetchReviewsFromDataset = async (datasetId) => {
    try {
      setLoading(true);
      console.log(`Fetching reviews from dataset: ${datasetId}`);
      
      const response = await api.get(`/api/dataset/${datasetId}/reviews`);
      if (response.data.success && response.data.reviews) {
        setReviews(response.data.reviews);
        console.log(`Successfully fetched ${response.data.reviews.length} reviews from dataset ${datasetId}`);
      } else {
        console.error('Failed to fetch reviews from dataset');
      }
    } catch (error) {
      console.error('Error fetching reviews from dataset:', error);
    } finally {
      setLoading(false);
    }
  };

  // Remove the old filterAndSortReviews function since filtering is now handled by the backend

  const handleRowClick = (review) => {
    setSelectedReview(review);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedReview(null);
  };

  const openCalendar = () => {
    setShowCalendar(true);
  };

  const closeCalendar = () => {
    setShowCalendar(false);
  };

  const handleDateFilter = (e) => {
    e.preventDefault(); // Prevent form submission
    console.log('Applying date filter:', { startDate, endDate, dateFilterType });
    
    // Apply the selected dates to the actual filter
    if (dateFilterType === 'single') {
      // For single date, use startDate for both start and end
      setAppliedStartDate(startDate);
      setAppliedEndDate(startDate);
    } else {
      // For range, use both dates
      setAppliedStartDate(startDate);
      setAppliedEndDate(endDate);
    }
    
    setCurrentPage(1); // Reset to first page when filtering
    closeCalendar();
    console.log('Date filter applied, will trigger fetchReviews');
  };

  const handleSearchInput = (value) => {
    setSearchInput(value);
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // Set new timeout for debounced search
    const timeout = setTimeout(() => {
      setSearchTerm(value);
      setCurrentPage(1);
    }, 500); // Wait 500ms after user stops typing
    
    setSearchTimeout(timeout);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Clear timeout and search immediately
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }
  };

  const clearDateFilter = () => {
    setStartDate('');
    setEndDate('');
    setAppliedStartDate('');
    setAppliedEndDate('');
    setCurrentPage(1);
  };

  // Pagination functions
  const changePage = (page) => {
    setCurrentPage(page);
    // Auto-scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (newPageSize) => {
    setReviewsPerPage(parseInt(newPageSize));
    setCurrentPage(1);
  };



  // eslint-disable-next-line no-unused-vars
  const formatDateForInput = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().split('T')[0];
  };

  // Get date range based on export settings
  const getExportDateRange = () => {
    // Use the actual current date
    const today = getCurrentDate();
    
    console.log('Date calculation using current date:', {
      now: new Date().toISOString(),
      today: today.toISOString(),
      year: today.getFullYear(),
      month: today.getMonth(),
      day: today.getDate()
    });
    
    switch (exportDateType) {
      case 'quick':
        switch (exportQuickDate) {
          case 'today':
            return { start: today, end: today };
          case 'yesterday':
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            console.log('Yesterday calculated:', yesterday.toISOString());
            return { start: yesterday, end: yesterday };
          case 'week':
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return { start: weekAgo, end: today };
          case 'month':
            const monthAgo = new Date(today);
            monthAgo.setDate(monthAgo.getDate() - 30);
            return { start: monthAgo, end: today };
          case 'quarter':
            const quarterAgo = new Date(today);
            quarterAgo.setDate(quarterAgo.getDate() - 90);
            return { start: quarterAgo, end: today };
          case 'year':
            const yearAgo = new Date(today);
            yearAgo.setDate(yearAgo.getDate() - 365);
            return { start: yearAgo, end: today };
          default:
            return { start: null, end: null };
        }
      case 'custom':
        if (exportCustomDate) {
          const customDate = new Date(exportCustomDate);
          return { start: customDate, end: customDate };
        }
        return { start: null, end: null };
      case 'range':
        if (exportStartDate && exportEndDate) {
          return { 
            start: new Date(exportStartDate), 
            end: new Date(exportEndDate) 
          };
        }
        return { start: null, end: null };
      default:
        return { start: null, end: null };
    }
  };

  // Fetch reviews for export based on filters
  const fetchReviewsForExport = async () => {
    try {
      setExportLoading(true);
      
      const dateRange = getExportDateRange();
      const branchIds = exportAllBranches ? [] : exportSelectedBranches;
      
      // Create proper date range for the entire day
      let startDateParam = null;
      let endDateParam = null;
      
      if (dateRange.start) {
        // For start date, use the beginning of the day in local timezone
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        startDateParam = startOfDay.toISOString();
        console.log('Start date calculation:', {
          original: dateRange.start.toISOString(),
          startOfDay: startOfDay.toISOString(),
          localString: startOfDay.toLocaleDateString()
        });
      }
      
      if (dateRange.end) {
        // For end date, use the end of the day in local timezone
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        endDateParam = endOfDay.toISOString();
        console.log('End date calculation:', {
          original: dateRange.end.toISOString(),
          endOfDay: endOfDay.toISOString(),
          localString: endOfDay.toLocaleDateString()
        });
      }
      
      const params = new URLSearchParams({
        sortBy: 'newest',
        ...(startDateParam && { startDate: startDateParam }),
        ...(endDateParam && { endDate: endDateParam }),
        ...(branchIds.length > 0 && { branchIds: branchIds.join(',') })
      });
      
      console.log('Export params:', params.toString());
      console.log('Date range:', { start: startDateParam, end: endDateParam });
      console.log('Original dateRange:', dateRange);
      
      const response = await api.get(`/api/export-reviews?${params}`);
      
      if (response.data.success) {
        console.log(`Export found ${response.data.reviews.length} reviews`);
        return response.data.reviews;
      } else {
        throw new Error('Failed to fetch reviews for export');
      }
    } catch (error) {
      console.error('Error fetching reviews for export:', error);
      throw error;
    } finally {
      setExportLoading(false);
    }
  };

  // Export to Excel
  const exportToExcel = async (reviewsData) => {
    const exportData = reviewsData.map(review => {
      const row = {};
      Object.keys(exportColumns).forEach(key => {
        if (exportColumns[key]) {
          const columnName = availableColumns[key];
          let value = review[key];
          
          // Handle special cases
          if (key === 'is_local_guide') {
            value = value ? 'Yes' : 'No';
          } else if (key === 'review_image_urls' && Array.isArray(value)) {
            value = value.join(', ');
          } else if (key === 'published_at_date' && value) {
            value = new Date(value).toLocaleDateString();
          } else if (key === 'response_from_owner_date' && value) {
            value = new Date(value).toLocaleDateString();
          } else if (key === 'branch_name' && review.branches) {
            value = review.branches.name || 'No Branch';
          }
          
          row[columnName] = value || 'N/A';
        }
      });
      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Google Reviews');
    
    // Auto-size columns
    const colWidths = Object.keys(exportColumns).filter(key => exportColumns[key]).map(() => ({ wch: 20 }));
    ws['!cols'] = colWidths;

    const dateRange = getExportDateRange();
    const dateSuffix = dateRange.start ? 
      `-${dateRange.start.toISOString().split('T')[0]}` : '';
    
    XLSX.writeFile(wb, `google-reviews${dateSuffix}.xlsx`);
  };

  // Export to CSV
  const exportToCSV = (reviewsData) => {
    const exportData = reviewsData.map(review => {
      const row = {};
      Object.keys(exportColumns).forEach(key => {
        if (exportColumns[key]) {
          const columnName = availableColumns[key];
          let value = review[key];
          
          // Handle special cases
          if (key === 'is_local_guide') {
            value = value ? 'Yes' : 'No';
          } else if (key === 'review_image_urls' && Array.isArray(value)) {
            value = value.join(', ');
          } else if (key === 'published_at_date' && value) {
            value = new Date(value).toLocaleDateString();
          } else if (key === 'response_from_owner_date' && value) {
            value = new Date(value).toLocaleDateString();
          } else if (key === 'branch_name' && review.branches) {
            value = review.branches.name || 'No Branch';
          }
          
          row[columnName] = value || 'N/A';
        }
      });
      return row;
    });

    const headers = Object.keys(exportData[0] || {});
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header] || '';
          // Escape commas and quotes
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    
    const dateRange = getExportDateRange();
    const dateSuffix = dateRange.start ? 
      `-${dateRange.start.toISOString().split('T')[0]}` : '';
    
    link.setAttribute('download', `google-reviews${dateSuffix}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export to PDF
  const exportToPDF = (reviewsData) => {
    try {
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(20);
      doc.text('Google Reviews Export', 20, 20);
      
      // Add export info
      doc.setFontSize(12);
      const dateRange = getExportDateRange();
      const dateInfo = dateRange.start ? 
        `Date Range: ${dateRange.start.toLocaleDateString()} - ${dateRange.end ? dateRange.end.toLocaleDateString() : 'Present'}` :
        'All Time';
      doc.text(dateInfo, 20, 35);
      
      doc.text(`Total Reviews: ${reviewsData.length}`, 20, 45);
      doc.text(`Export Date: ${new Date().toLocaleDateString()}`, 20, 55);
      
      // Prepare table data
      const selectedColumns = Object.keys(exportColumns).filter(key => exportColumns[key]);
      const headers = selectedColumns.map(key => availableColumns[key]);
      
      const tableData = reviewsData.map(review => {
        return selectedColumns.map(key => {
          let value = review[key];
          
          // Handle special cases
          if (key === 'is_local_guide') {
            value = value ? 'Yes' : 'No';
          } else if (key === 'review_image_urls' && Array.isArray(value)) {
            value = value.length > 0 ? `${value.length} images` : 'No images';
          } else if (key === 'published_at_date' && value) {
            value = new Date(value).toLocaleDateString();
          } else if (key === 'response_from_owner_date' && value) {
            value = new Date(value).toLocaleDateString();
          } else if (key === 'branch_name' && review.branches) {
            value = review.branches.name || 'No Branch';
          }
          
          return value || 'N/A';
        });
      });
      
      // Add table
      doc.autoTable({
        head: [headers],
        body: tableData,
        startY: 70,
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [16, 185, 129],
          textColor: 255
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        }
      });
      
      // Save the PDF
      const dateSuffix = dateRange.start ? 
        `-${dateRange.start.toISOString().split('T')[0]}` : '';
      
      doc.save(`google-reviews${dateSuffix}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again or use Excel/CSV export.');
    }
  };

  // Handle export
  const handleExport = async () => {
    try {
      // Check if at least one column is selected
      const selectedColumnsCount = Object.values(exportColumns).filter(Boolean).length;
      if (selectedColumnsCount === 0) {
        alert('Please select at least one column to export.');
        return;
      }

      const reviewsData = await fetchReviewsForExport();
      
      if (reviewsData.length === 0) {
        alert('No reviews found matching your criteria.');
        return;
      }

      switch (exportFormat) {
        case 'excel':
          exportToExcel(reviewsData);
          break;
        case 'csv':
          exportToCSV(reviewsData);
          break;
        case 'pdf':
          exportToPDF(reviewsData);
          break;
        default:
          exportToExcel(reviewsData);
      }
      
      setShowExportPopup(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  // Toggle column selection
  const toggleColumn = (columnKey) => {
    setExportColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Select all columns
  const selectAllColumns = () => {
    const allColumns = {};
    Object.keys(availableColumns).forEach(key => {
      allColumns[key] = true;
    });
    setExportColumns(allColumns);
  };

  // Deselect all columns
  const deselectAllColumns = () => {
    const noColumns = {};
    Object.keys(availableColumns).forEach(key => {
      noColumns[key] = false;
    });
    setExportColumns(noColumns);
  };

  // Toggle branch selection
  const toggleBranchSelection = (branchId) => {
    if (exportSelectedBranches.includes(branchId)) {
      setExportSelectedBranches(prev => prev.filter(id => id !== branchId));
    } else {
      setExportSelectedBranches(prev => [...prev, branchId]);
    }
  };

  // Toggle all branches
  const toggleAllBranches = () => {
    if (exportAllBranches) {
      setExportAllBranches(false);
      setExportSelectedBranches([]);
    } else {
      setExportAllBranches(true);
      setExportSelectedBranches(branches.map(b => b.id));
    }
  };

  const renderStars = (rating) => {
    if (!rating) return 'N/A';
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Pagination is now handled by the backend database

  if (loading) {
    return (
      <div className="reviews-loading">
        <div className="loading-spinner"></div>
        <p>Loading reviews...</p>
      </div>
    );
  }

  return (
    <div className="reviews">
      <div className="reviews-header">
        <div className="header-content">
          <h1 className="reviews-title">Reviews Management</h1>
          <p className="reviews-subtitle">
            {reviews.length} reviews found • {totalCount} total reviews
          </p>
        </div>
        <button 
          className="export-btn glass-effect hover-glow"
          onClick={() => setShowExportPopup(true)}
          disabled={reviews.length === 0}
        >
          <Download size={16} />
          Export Data
        </button>
      </div>

      {/* Filters Section - Collapsible on Mobile */}
      <div className="filters-section glass-effect">
        {/* Mobile Filter Toggle Button */}
        <div className="mobile-filter-toggle">
          <button 
            className="filter-toggle-btn"
            onClick={() => setFiltersExpanded(!filtersExpanded)}
          >
            <Search size={20} />
            <span>{filtersExpanded ? 'Hide Filters' : 'Show Filters'}</span>
            {filtersExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
        
        <div className={`filters-row ${filtersExpanded ? 'expanded' : 'collapsed'}`}>

          <div className="filter-controls">
            {(appliedStartDate || appliedEndDate) && (
              <div className="active-date-filter">
                <span className="filter-badge">
                  📅 {appliedStartDate && appliedEndDate ? `${appliedStartDate} - ${appliedEndDate}` : appliedStartDate || appliedEndDate}
                </span>
              </div>
            )}
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="filter-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="highest">Highest Rating</option>
              <option value="lowest">Lowest Rating</option>
              <option value="mostRelevant">Most Relevant</option>
            </select>

            <select 
              value={language} 
              onChange={(e) => setLanguage(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Languages</option>
              <option value="en">English</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="ar">Arabic</option>
            </select>

            <select 
              value={ratingFilter} 
              onChange={(e) => setRatingFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Ratings</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>

            <select 
              value={selectedBranches.length === 1 ? selectedBranches[0] : ''} 
              onChange={(e) => {
                if (e.target.value === '') {
                  setSelectedBranches([]);
                } else {
                  setSelectedBranches([e.target.value]);
                }
              }}
              className="filter-select"
            >
              <option value="">All Branches</option>
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.name} {branch.city && `(${branch.city})`}
                </option>
              ))}
            </select>

            <button 
              className="calendar-filter-btn glass-effect hover-glow"
              onClick={openCalendar}
              title="Filter by date range"
            >
              <Calendar size={16} />
              {appliedStartDate || appliedEndDate ? 'Date Filter Active' : 'Date Filter'}
            </button>
          </div>
        </div>

      </div>

      {/* Calendar Filter Modal */}
      {showCalendar && (
        <div className="calendar-modal-overlay" onClick={closeCalendar}>
          <motion.div 
            className="calendar-modal glass-effect"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="calendar-modal-header">
              <h3>Filter Reviews by Date</h3>
              <button className="close-btn" onClick={closeCalendar}>×</button>
            </div>
            
            <div className="calendar-modal-content">
              <div className="date-filter-type">
                <label>
                  <input
                    type="radio"
                    name="dateFilterType"
                    value="single"
                    checked={dateFilterType === 'single'}
                    onChange={(e) => setDateFilterType(e.target.value)}
                  />
                  Single Date
                </label>
                <label>
                  <input
                    type="radio"
                    name="dateFilterType"
                    value="range"
                    checked={dateFilterType === 'range'}
                    onChange={(e) => setDateFilterType(e.target.value)}
                  />
                  Date Range
                </label>
              </div>

              {dateFilterType === 'single' ? (
                <div className="single-date-input">
                  <label>Select Date:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => {
                      e.preventDefault();
                      setStartDate(e.target.value);
                    }}
                    className="date-input"
                  />
                </div>
              ) : (
                <div className="date-range-inputs">
                  <div className="date-input-group">
                    <label>From Date:</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => {
                        e.preventDefault();
                        setStartDate(e.target.value);
                      }}
                      className="date-input"
                    />
                  </div>
                  <div className="date-input-group">
                    <label>To Date:</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => {
                        e.preventDefault();
                        setEndDate(e.target.value);
                      }}
                      className="date-input"
                    />
                  </div>
                </div>
              )}

              <div className="calendar-modal-actions">
                <button 
                  className="apply-filter-btn glass-effect hover-glow"
                  onClick={handleDateFilter}
                  disabled={!startDate}
                >
                  Apply Filter
                </button>
                {(startDate || endDate) && (
                  <button 
                    className="clear-filter-btn glass-effect hover-glow"
                    onClick={clearDateFilter}
                  >
                    Clear Filter
                  </button>
                )}
              </div>

              {(startDate || endDate) && (
                <div className="active-filter-info">
                  <p>
                    <strong>Active Filter:</strong> 
                    {dateFilterType === 'single' 
                      ? ` Reviews from ${startDate}`
                      : ` Reviews from ${startDate || 'any date'} to ${endDate || 'any date'}`
                    }
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Search Field - Between Filters and Table */}
      <div className="search-section glass-effect">
        <div className="search-container">
          <Search size={20} className="search-icon" />
          <input
            type="text"
            placeholder="Search reviews, reviewers, or places..."
            value={searchInput}
            onChange={(e) => {
              e.preventDefault();
              handleSearchInput(e.target.value);
            }}
            onKeyPress={handleSearchKeyPress}
            className="search-input"
          />
        </div>
      </div>

      {/* Reviews Table */}
      <div className="reviews-table-container glass-effect">
        <table className="reviews-table">
          <thead>
            <tr>
              <th>Reviewer</th>
              <th>Rating</th>
              <th>Review</th>
              <th>Date</th>
              <th>Place</th>
              <th>Branch</th>
              <th>Language</th>
              <th>Response</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((review, index) => (
              <motion.tr 
                key={`${review.review_id}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="review-row clickable-row"
                onClick={() => handleRowClick(review)}
              >
                <td className="reviewer-cell">
                  <div className="table-reviewer-info">
                    <div>
                      <div className="table-reviewer-name">{review.reviewer_name || 'Anonymous'}</div>
                      {review.is_local_guide && (
                        <span className="table-local-guide-badge">Local Guide</span>
                      )}
                      <div className="table-reviewer-stats">
                        {review.reviewer_number_of_reviews > 0 && (
                          <span className="table-reviewer-reviews-count">{review.reviewer_number_of_reviews} reviews</span>
                        )}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="rating-cell">
                  <div className="rating-display">
                    <span className="stars">{renderStars(review.stars)}</span>
                  </div>
                </td>
                <td className="review-text-cell">
                  <div className="review-text">
                    {review.review_text ? (
                      review.review_text.length > 200 ? 
                        `${review.review_text.substring(0, 200)}...` : 
                        review.review_text
                      ) : review.review_text_translated ? (
                        review.review_text_translated.length > 200 ? 
                          `${review.review_text_translated.substring(0, 200)}...` : 
                          review.review_text_translated
                      ) : (
                        'No review text'
                      )}
                    </div>
                    {review.review_image_urls && review.review_image_urls.length > 0 && (
                      <div className="review-images">
                        <span className="image-count">📷 {review.review_image_urls.length} images</span>
                      </div>
                    )}
                  </td>
                  <td className="date-cell">
                    {formatDate(review.published_at_date)}
                  </td>
                  <td className="place-cell">
                    <div className="place-info">
                      <div className="place-name">{review.place_title || 'N/A'}</div>
                      <div className="place-address">{review.city}, {review.country_code}</div>
                    </div>
                  </td>
                  <td className="branch-cell">
                    <div className="branch-info">
                      {review.branches ? (
                        <div className="branch-name">{review.branches.name}</div>
                      ) : (
                        <span className="no-branch">No branch</span>
                      )}
                    </div>
                  </td>
                  <td className="language-cell">
                    {review.original_language || 'en'}
                  </td>
                  <td className="response-cell">
                    {review.response_from_owner_text ? (
                      <div className="owner-response">
                        <span className="response-indicator">💬</span>
                        <span className="response-text">
                          {review.response_from_owner_text.length > 50 ? 
                            `${review.response_from_owner_text.substring(0, 50)}...` : 
                            review.response_from_owner_text
                          }
                        </span>
                      </div>
                    ) : (
                      <span className="no-response">No response</span>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>

        {reviews.length === 0 && (
          <div className="no-reviews">
            <MessageSquare size={48} />
            <p>
              {appliedStartDate || appliedEndDate 
                ? `No reviews found for the selected date range${appliedStartDate && appliedEndDate ? ` (${appliedStartDate} to ${appliedEndDate})` : appliedStartDate ? ` (${appliedStartDate})` : ` (up to ${appliedEndDate})`}`
                : 'No reviews found matching your criteria'
              }
            </p>
            {(appliedStartDate || appliedEndDate) && (
              <button 
                className="clear-date-filter-btn glass-effect hover-glow"
                onClick={clearDateFilter}
              >
                Clear Date Filter
              </button>
            )}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {reviews.length > 0 && (
        <div className="pagination-controls">
          <div className="pagination-header">
            <div className="pagination-info">
              <span>Showing {reviews.length} of {totalCount} reviews</span>
              <span>Page {currentPage} of {totalPages}</span>
            </div>
            <div className="page-size-selector">
              <label>Reviews per page:</label>
              <select 
                value={reviewsPerPage} 
                onChange={(e) => handlePageSizeChange(e.target.value)}
                className="page-size-select"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
          {totalPages > 1 && (
            <div className="pagination-buttons">
              <button 
                onClick={() => changePage(1)} 
                disabled={loading || currentPage === 1}
                className="pagination-btn"
              >
                First
              </button>
              <button 
                onClick={() => changePage(Math.max(1, currentPage - 1))} 
                disabled={loading || currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              <span className="page-info">{currentPage} / {totalPages}</span>
              <button 
                onClick={() => changePage(Math.min(totalPages, currentPage + 1))} 
                disabled={loading || currentPage >= totalPages}
                className="pagination-btn"
              >
                Next
              </button>
              <button 
                onClick={() => changePage(totalPages)} 
                disabled={loading || currentPage === totalPages}
                className="pagination-btn"
              >
                Last
              </button>
            </div>
          )}
          {loading && (
            <div className="pagination-loading">
              <RefreshCw size={16} className="spinning" />
              <span>Loading reviews...</span>
            </div>
          )}
        </div>
      )}

      {/* Review Details Modal */}
      {showModal && selectedReview && (
        <div className="modal-overlay" onClick={closeModal}>
          <motion.div 
            className="review-modal glass-effect"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Review Details</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            
            <div className="modal-content">
              {/* Reviewer Info */}
              <div className="reviewer-section">
                <div className="reviewer-header">
                  <div className="reviewer-avatar">
                    {selectedReview.reviewer_photo_url ? (
                      <img src={selectedReview.reviewer_photo_url} alt="Reviewer" />
                    ) : (
                      <div className="avatar-placeholder">
                        <User size={24} />
                      </div>
                    )}
                  </div>
                  <div className="reviewer-info">
                    <h3>{selectedReview.reviewer_name || 'Anonymous'}</h3>
                    <div className="reviewer-stats">
                      {selectedReview.is_local_guide && <span className="local-guide">🏆 Local Guide</span>}
                      <span className="review-count">{selectedReview.reviewer_number_of_reviews || 0} reviews</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Rating and Review */}
              <div className="rating-section">
                <div className="stars-display">
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star 
                      key={star} 
                      size={20} 
                      className={star <= selectedReview.stars ? 'star-filled' : 'star-empty'} 
                    />
                  ))}
                  <span className="rating-text">{selectedReview.stars}/5</span>
                </div>
                
                {selectedReview.review_title && (
                  <h4 className="review-title">{selectedReview.review_title}</h4>
                )}
                
                <div className="review-text-full">
                  {selectedReview.review_text || selectedReview.review_text_translated || 'No review text available'}
                </div>
              </div>

              {/* Response from Owner */}
              {selectedReview.response_from_owner_text && (
                <div className="owner-response-section">
                  <h4>💬 Owner Response</h4>
                  <div className="response-text-full">
                    {selectedReview.response_from_owner_text}
                  </div>
                  {selectedReview.response_from_owner_date && (
                    <div className="response-date">
                      {formatDate(selectedReview.response_from_owner_date)}
                    </div>
                  )}
                </div>
              )}

              {/* Review Images */}
              {selectedReview.review_image_urls && selectedReview.review_image_urls.length > 0 && (
                <div className="images-section">
                  <h4>📷 Review Images ({selectedReview.review_image_urls.length})</h4>
                  <div className="image-gallery">
                    {selectedReview.review_image_urls.map((url, index) => (
                      <img 
                        key={index} 
                        src={url} 
                        alt={`Review ${index + 1}`}
                        className="review-image"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Details */}
              <div className="details-section">
                <div className="detail-row">
                  <span className="detail-label">Published:</span>
                  <span className="detail-value">{formatDate(selectedReview.published_at_date)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Language:</span>
                  <span className="detail-value">{selectedReview.original_language || 'Unknown'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Place:</span>
                  <span className="detail-value">{selectedReview.place_title || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Location:</span>
                  <span className="detail-value">{selectedReview.city}, {selectedReview.country_code}</span>
                </div>
                {selectedReview.likes_count > 0 && (
                  <div className="detail-row">
                    <span className="detail-label">Likes:</span>
                    <span className="detail-value">{selectedReview.likes_count}</span>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Export Popup */}
      {showExportPopup && (
        <div className="export-popup-overlay" onClick={() => setShowExportPopup(false)}>
          <motion.div 
            className="export-popup glass-effect"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="export-popup-header">
              <h2>Export Reviews Data</h2>
              <button className="close-btn" onClick={() => setShowExportPopup(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="export-popup-content">
              {/* Date Selection */}
              <div className="export-section">
                <h3>📅 Date Range</h3>
                <div className="date-type-selector">
                  <label>
                    <input
                      type="radio"
                      name="exportDateType"
                      value="all"
                      checked={exportDateType === 'all'}
                      onChange={(e) => setExportDateType(e.target.value)}
                    />
                    All Time
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="exportDateType"
                      value="quick"
                      checked={exportDateType === 'quick'}
                      onChange={(e) => setExportDateType(e.target.value)}
                    />
                    Quick Selection
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="exportDateType"
                      value="custom"
                      checked={exportDateType === 'custom'}
                      onChange={(e) => setExportDateType(e.target.value)}
                    />
                    Custom Date
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="exportDateType"
                      value="range"
                      checked={exportDateType === 'range'}
                      onChange={(e) => setExportDateType(e.target.value)}
                    />
                    Date Range
                  </label>
                </div>

                              {/* Quick Date Buttons */}
              {exportDateType === 'quick' && (
                <div className="quick-date-buttons">
                  {quickDateOptions.map(option => (
                    <button
                      key={option.value}
                      className={`quick-date-btn ${exportQuickDate === option.value ? 'active' : ''}`}
                      onClick={() => setExportQuickDate(option.value)}
                    >
                      {option.icon} {option.label}
                    </button>
                  ))}
                </div>
              )}

                {/* Custom Date Input */}
                {exportDateType === 'custom' && (
                  <div className="custom-date-input">
                    <label>Select Date:</label>
                    <input
                      type="date"
                      value={exportCustomDate}
                      onChange={(e) => setExportCustomDate(e.target.value)}
                      className="date-input"
                    />
                  </div>
                )}

                {/* Date Range Inputs */}
                {exportDateType === 'range' && (
                  <div className="date-range-inputs">
                    <div className="date-input-group">
                      <label>From Date:</label>
                      <input
                        type="date"
                        value={exportStartDate}
                        onChange={(e) => setExportStartDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                    <div className="date-input-group">
                      <label>To Date:</label>
                      <input
                        type="date"
                        value={exportEndDate}
                        onChange={(e) => setExportEndDate(e.target.value)}
                        className="date-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Branch Selection */}
              <div className="export-section">
                <h3>🏢 Branch Selection</h3>
                <div className="branch-selection">
                  <label className="branch-toggle">
                    <input
                      type="checkbox"
                      checked={exportAllBranches}
                      onChange={toggleAllBranches}
                    />
                    Export from all branches
                  </label>
                  
                  {!exportAllBranches && (
                    <div className="branch-checkboxes">
                      {branches.map(branch => (
                        <label key={branch.id} className="branch-checkbox">
                          <input
                            type="checkbox"
                            checked={exportSelectedBranches.includes(branch.id)}
                            onChange={() => toggleBranchSelection(branch.id)}
                          />
                          {branch.name} {branch.city && `(${branch.city})`}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Column Selection */}
              <div className="export-section">
                <h3>📊 Data Columns</h3>
                <div className="column-selection-header">
                  <button 
                    className="column-action-btn glass-effect"
                    onClick={selectAllColumns}
                  >
                    Select All
                  </button>
                  <button 
                    className="column-action-btn glass-effect"
                    onClick={deselectAllColumns}
                  >
                    Deselect All
                  </button>
                  <button 
                    className="column-toggle-btn glass-effect"
                    onClick={() => setShowColumnSelector(!showColumnSelector)}
                  >
                    {showColumnSelector ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    {showColumnSelector ? 'Hide' : 'Show'} Column Selector
                  </button>
                </div>

                {showColumnSelector && (
                  <div className="column-selector">
                    <div className="column-grid">
                      {Object.entries(availableColumns).map(([key, label]) => (
                        <label key={key} className="column-checkbox">
                          <input
                            type="checkbox"
                            checked={exportColumns[key] || false}
                            onChange={() => toggleColumn(key)}
                          />
                          <span className="checkbox-label">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="selected-columns-summary">
                  <strong>Selected:</strong> {Object.values(exportColumns).filter(Boolean).length} of {Object.keys(availableColumns).length} columns
                </div>
                
                {/* Preview of selected columns */}
                <div className="selected-columns-preview">
                  <strong>Preview:</strong>
                  <div className="preview-tags">
                    {Object.entries(exportColumns)
                      .filter(([key, selected]) => selected)
                      .map(([key, selected]) => (
                        <span key={key} className="preview-tag">
                          {availableColumns[key]}
                        </span>
                      ))}
                  </div>
                </div>
              </div>

              {/* Export Format */}
              <div className="export-section">
                <h3>💾 Export Format</h3>
                <div className="format-selector">
                  {exportFormats.map(format => (
                    <label key={format.value} className="format-option">
                      <input
                        type="radio"
                        name="exportFormat"
                        value={format.value}
                        checked={exportFormat === format.value}
                        onChange={(e) => setExportFormat(e.target.value)}
                      />
                      <format.icon size={16} />
                      {format.label}
                    </label>
                  ))}
                </div>
              </div>



              {/* Export Summary */}
              <div className="export-section export-summary">
                <h3>📋 Export Summary</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Date Range:</span>
                    <span className="summary-value">
                      {(() => {
                        const dateRange = getExportDateRange();
                        if (dateRange.start) {
                          return `${dateRange.start.toLocaleDateString()} - ${dateRange.end ? dateRange.end.toLocaleDateString() : 'Present'}`;
                        }
                        return 'All Time';
                      })()}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Branches:</span>
                    <span className="summary-value">
                      {exportAllBranches ? 'All Branches' : `${exportSelectedBranches.length} Selected`}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Columns:</span>
                    <span className="summary-value">
                      {Object.values(exportColumns).filter(Boolean).length} of {Object.keys(availableColumns).length}
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Format:</span>
                    <span className="summary-value">
                      {exportFormats.find(f => f.value === exportFormat)?.label}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="export-popup-actions">
              <button 
                className="cancel-btn glass-effect"
                onClick={() => setShowExportPopup(false)}
                disabled={exportLoading}
              >
                Cancel
              </button>
              <button 
                className="export-action-btn glass-effect hover-glow"
                onClick={handleExport}
                disabled={exportLoading}
              >
                {exportLoading ? (
                  <>
                    <RefreshCw size={16} className="spinning" />
                    Preparing Export...
                  </>
                ) : (
                  <>
                    <Download size={16} />
                    Export Data
                  </>
                )}
              </button>
            </div>
            
            {/* Export Progress */}
            {exportLoading && (
              <div className="export-progress">
                <div className="progress-bar">
                  <div className="progress-fill"></div>
                </div>
                <p>Fetching reviews and preparing export...</p>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Reviews;
